// src/pages/CategoryPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import products from '../data/products';
import './CategoryPage.css';

const CategoryPage = () => {
  const { category } = useParams();
  const [selectedStrain, setSelectedStrain] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterSticky, setIsFilterSticky] = useState(false);
  const productsPerPage = 12;
  
  // Ref for filter bar
  const filterBarRef = useRef(null);

  // Scroll to top on mount and when category changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Reset filters when category changes
    setSelectedStrain('all');
    setSortBy('default');
    setCurrentPage(1);
  }, [category]);

  // Handle filter bar sticky effect
  useEffect(() => {
    const handleScroll = () => {
      if (filterBarRef.current) {
        const filterBarTop = filterBarRef.current.offsetTop;
        setIsFilterSticky(window.scrollY > filterBarTop - 80);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to filter bar
  const scrollToFilters = useCallback(() => {
    if (filterBarRef.current) {
      const yOffset = -20;
      const y = filterBarRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  }, []);

  // Get category products
  const categoryProducts = products.filter(p => p.category === category);
  
  // Get unique strains with counts
  const strains = useCallback(() => {
    const strainMap = new Map();
    categoryProducts.forEach(p => {
      if (p.strain) {
        strainMap.set(p.strain, (strainMap.get(p.strain) || 0) + 1);
      }
    });
    return [['all', categoryProducts.length], ...Array.from(strainMap.entries())];
  }, [categoryProducts]);

  const strainList = strains();

  // Filter and sort products
  const filteredProducts = useCallback(() => {
    let filtered = selectedStrain === 'all' 
      ? categoryProducts 
      : categoryProducts.filter(p => p.strain === selectedStrain);

    // Apply sorting
    switch(sortBy) {
      case 'price-low':
        filtered.sort((a, b) => {
          const priceA = a.priceOptions?.[0]?.price || a.price || 0;
          const priceB = b.priceOptions?.[0]?.price || b.price || 0;
          return priceA - priceB;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceA = a.priceOptions?.[0]?.price || a.price || 0;
          const priceB = b.priceOptions?.[0]?.price || b.price || 0;
          return priceB - priceA;
        });
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        // Default sorting (featured)
        filtered.sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));
    }

    return filtered;
  }, [categoryProducts, selectedStrain, sortBy]);

  const results = filteredProducts();
  
  // Pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = results.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(results.length / productsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    scrollToFilters();
  }, [selectedStrain, sortBy, scrollToFilters]);

  const getCategoryName = (cat) => {
    return cat.replace('-', ' ');
  };

  const getCategoryIcon = (cat) => {
    switch(cat) {
      case 'Flower': return '🌿';
      case 'Exotic-Flower': return '🌸';
      case 'Edibles': return '🍬';
      case 'Concentrates': return '⚗️';
      case 'cart': return '🔋';
      case 'disposable-cart': return '💨';
      case 'Pre-rolls': return '🚬';
      case 'Shake': return '🍃';
      default: return '📦';
    }
  };

  const getCategoryDescription = (cat) => {
    switch(cat) {
      case 'Flower':
        return 'Premium cannabis flower available in various strains and quantities. Lab-tested for potency and purity.';
      case 'Exotic-Flower':
        return 'Top-shelf exotic strains for the discerning connoisseur. Limited quantities of rare genetics.';
      case 'Edibles':
        return 'Delicious THC-infused edibles in various forms and potencies. Perfect for discreet consumption.';
      case 'Concentrates':
        return 'High-potency concentrates for experienced users. Includes wax, shatter, and live resin.';
      case 'cart':
        return '510-thread vape cartridges with premium extracts. Compatible with standard batteries.';
      case 'disposable-cart':
        return 'Convenient disposable vape pens in various strains. No battery needed - ready to use.';
      case 'Pre-rolls':
        return 'Pre-rolled joints for instant enjoyment. Premium flower rolled to perfection.';
      case 'Shake':
        return 'Premium shake at great value. Perfect for rolling or packing your own.';
      default:
        return `Browse our selection of premium ${getCategoryName(cat)} products.`;
    }
  };

  const getStrainColor = (strain) => {
    switch(strain) {
      case 'Sativa': return 'var(--strain-sativa)';
      case 'Indica': return 'var(--strain-indica)';
      case 'Hybrid': return 'var(--strain-hybrid)';
      default: return 'var(--primary-color)';
    }
  };

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    if (filterBarRef.current) {
      const yOffset = -20;
      const y = filterBarRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  }, []);

  return (
    <div className="category-page">
      {/* Filter Bar */}
      <div 
        ref={filterBarRef}
        className={`filter-bar ${isFilterSticky ? 'sticky' : ''}`}
      >
        <div className="container">
          <div className="filter-bar-content">
            <div className="filter-bar-left">
              {/* Breadcrumb */}
              <nav className="breadcrumb">
                <Link to="/" className="breadcrumb-link">Home</Link>
                <span className="breadcrumb-separator">/</span>
                <Link to="/categories" className="breadcrumb-link">Categories</Link>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-current">{getCategoryName(category)}</span>
              </nav>

              <button 
                className="filter-toggle"
                onClick={() => setShowFilters(!showFilters)}
                aria-expanded={showFilters}
              >
                <svg className="filter-icon" viewBox="0 0 24 24" fill="none">
                  <path d="M3 4h18M3 12h18M3 20h18M8 8v8M16 8v8" 
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>Strain</span>
                {selectedStrain !== 'all' && (
                  <span className="filter-badge">1</span>
                )}
              </button>

              <div className="view-options">
                <button 
                  className={`view-option ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  aria-label="Grid view"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                    <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                    <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                    <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
                <button 
                  className={`view-option ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  aria-label="List view"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="4" stroke="currentColor" strokeWidth="2"/>
                    <rect x="3" y="10" width="18" height="4" stroke="currentColor" strokeWidth="2"/>
                    <rect x="3" y="17" width="18" height="4" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="filter-bar-right">
              <select 
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort by"
              >
                <option value="default">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
                <option value="rating">Top Rated</option>
              </select>

              <p className="results-count">
                <span className="count-number">{results.length}</span>
                <span className="count-label">products</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="category-section">
        <div className="container">
          {/* Category Header */}
          <div className="category-header">
            <div className="category-icon-large">{getCategoryIcon(category)}</div>
            <div className="category-info">
              <h1 className="category-title">{getCategoryName(category)}</h1>
              <p className="category-description">{getCategoryDescription(category)}</p>
              <div className="category-meta">
                <span className="meta-item">
                  <span className="meta-label">Products:</span>
                  <span className="meta-value">{categoryProducts.length}</span>
                </span>
                <span className="meta-item">
                  <span className="meta-label">Strains:</span>
                  <span className="meta-value">{strainList.length - 1}</span>
                </span>
              </div>
            </div>
            <Link to="/products" className="view-all-link">
              <span>View All Products</span>
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </Link>
          </div>

          <div className="products-layout">
            {/* Strain Filters Sidebar */}
            <aside className={`strain-sidebar ${showFilters ? 'open' : ''}`}>
              <div className="strain-header">
                <h3>Filter by Strain</h3>
                <button className="close-filters" onClick={() => setShowFilters(false)}>
                  ✕
                </button>
              </div>

              <div className="strain-list">
                {strainList.map(([strain, count]) => (
                  <button
                    key={strain}
                    className={`strain-chip ${selectedStrain === strain ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedStrain(strain);
                      setShowFilters(false);
                    }}
                    style={selectedStrain === strain ? {
                      '--strain-color': getStrainColor(strain)
                    } : {}}
                  >
                    <span className="strain-indicator" />
                    <span className="strain-name">
                      {strain === 'all' ? 'All Strains' : strain}
                    </span>
                    <span className="strain-count">{count}</span>
                  </button>
                ))}
              </div>

              {selectedStrain !== 'all' && (
                <button 
                  className="clear-strain"
                  onClick={() => {
                    setSelectedStrain('all');
                    setShowFilters(false);
                  }}
                >
                  Clear Filter
                </button>
              )}
            </aside>

            {/* Products Grid/List */}
            <main className="products-main">
              {results.length > 0 ? (
                <>
                  {/* Active Filters */}
                  {selectedStrain !== 'all' && (
                    <div className="active-filters">
                      <span className="active-filter-label">Active Filter:</span>
                      <button 
                        className="active-filter-chip"
                        onClick={() => setSelectedStrain('all')}
                      >
                        {selectedStrain}
                        <span className="remove-filter">✕</span>
                      </button>
                    </div>
                  )}

                  <div className={`products-container ${viewMode}`}>
                    {currentProducts.map(product => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                      
                      <div className="pagination-pages">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <h2>No products found</h2>
                  <p>Try selecting a different strain</p>
                  <button 
                    className="reset-btn"
                    onClick={() => setSelectedStrain('all')}
                  >
                    Show All Strains
                  </button>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* Mobile Overlay */}
      {showFilters && <div className="filters-overlay" onClick={() => setShowFilters(false)} />}

      {/* Back to Top Button */}
      <button
        className={`back-to-top ${isFilterSticky ? 'visible' : ''}`}
        onClick={() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }}
        aria-label="Back to top"
      >
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
};

export default CategoryPage;